import {
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { IconButton, ItemImageArea, SkeletonText } from 'utoniq-ui'

import { Item } from '@/clients/utoniq-core/schema'
import { ItemStatusCell } from '@/components/organisms'
import { styleSets } from '@/components/styles'
import { useTranslation } from '@/core'
import { Organism } from '@/core/architecture'

export type ItemSelectBoxProps = {
  checked?: boolean
  item: Pick<
    Item,
    | 'id'
    | 'title'
    | 'thumbnail'
    | 'issuedNumber'
    | 'salesCondition'
    | 'isDrm'
    | 'isPublished'
    | 'isPremium'
  >
  onSelect?: (itemId: string) => void
  onClose?: () => void
}

export const ItemSelectBox: Organism<ItemSelectBoxProps> = ({
  checked,
  item,
  onSelect,
  onClose,
}) => {
  const { t } = useTranslation('consoleMissionCreate')
  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: checked ? 'primary.main' : 'grey.50',
        position: 'relative',
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={4} alignItems="center">
          {checked !== undefined && (
            <Checkbox
              checked={checked}
              value={checked}
              disableRipple
              sx={{ '&.Mui-checked svg': { color: 'primary.main' } }}
            />
          )}
          <ItemImageArea
            size="small"
            src={item.thumbnail.front?.smallUri ?? ''}
          />
          <Stack flex={1} alignSelf="flex-start">
            <ItemStatusCell
              salesCondition={item.salesCondition}
              isPublished={item.isPublished}
              isDrm={item.isDrm}
              isPremium={item.isPremium}
            />
            <Typography variant="h6" sx={styleSets.lineCamp(2)}>
              {item.title}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ ...styleSets.lineCamp(2), mt: 1 }}
            >
              {t('issuedNumber', { number: item.issuedNumber ?? '∞' })}
            </Typography>
          </Stack>
          {onClose && (
            <Stack alignSelf="flex-start">
              <IconButton.Close size="xSmall" onClick={onClose} />
            </Stack>
          )}
        </Stack>
      </CardContent>
      {onSelect && (
        <CardActionArea
          onClick={() => onSelect(item.id)}
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 2,
          }}
        />
      )}
    </Card>
  )
}

export const ItemSelectBoxSkeleton: Organism = ({}) => (
  <Card elevation={0} sx={{ borderRadius: 2 }}>
    <CardContent>
      <Stack direction="row" spacing={4} alignItems="center">
        <Checkbox disabled />
        <Skeleton variant="rectangular" width={60} height={96} />
        <Stack flex={1} alignSelf="flex-start">
          <SkeletonText variant="medium" numberOfLine={2} />
          <Stack sx={{ pt: 1 }}>
            <SkeletonText variant="small" width="50%" />
          </Stack>
        </Stack>
      </Stack>
    </CardContent>
  </Card>
)
